/**
 * What the customer forms against the public API of the Platform share: „Odstoupit od smlouvy zde“ (ADR 0012,
 * bestio-withdrawal-form.js) and the complaint form (BEST-558, ADR 0016, bestio-complaint-form.js).
 *
 * Both call two POST endpoints of /api/public/v1 without cookies (lookup of the order, then the submission), keep one
 * submission id per statement in sessionStorage until the receipt, and answer an unreachable server with the e-mail. The
 * section loads this script before the form's own script (both deferred, so in document order); the contract tests run
 * them in one context in the same order.
 */
(() => {
  const TIMEOUT_MS = 15000;

  const format = (template, params = {}) => String(template ?? '')
    .replace(/\{(\w+)\}/g, (match, name) => (params[name] != null ? String(params[name]) : match));
  const text = value => (typeof value === 'string' ? value : '');

  /** The server could not be reached or did not answer in time; any other error is a program error and is not caught. */
  class NetworkError extends Error {}

  /**
   * The two calls of a public API under `path` (for example /api/public/v1/withdrawals): lookup posts to `{path}/lookup`,
   * submit to `{path}`. A response is {status, data}; an unreachable server or a timeout throws NetworkError.
   */
  function createApi({baseUrl, path, fetchImpl, timeoutMs = TIMEOUT_MS}) {
    const origin = String(baseUrl || '').replace(/\/+$/, '');
    async function call(suffix, body) {
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = controller && typeof setTimeout === 'function' ? setTimeout(() => controller.abort(), timeoutMs) : null;
      let response;
      try {
        response = await fetchImpl(`${origin}${path}${suffix}`, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
          referrerPolicy: 'no-referrer',
          headers: {Accept: 'application/json', 'Content-Type': 'application/json'},
          body: JSON.stringify(body),
          signal: controller?.signal,
        });
      } catch (error) {
        throw new NetworkError(error?.name === 'AbortError' ? 'timeout' : 'network');
      } finally {
        if (timer) clearTimeout(timer);
      }
      let data = null;
      try {
        const answer = await response.text();
        data = answer ? JSON.parse(answer) : null;
      } catch {
        data = null;
      }
      return {status: response.status, data};
    }
    return {lookup: body => call('/lookup', body), submit: body => call('', body)};
  }

  /**
   * What an answer means for a form. `prefix` is the API's code prefix (withdrawal, complaint): 422
   * `{prefix}_order_not_found` is an unknown order, any other 422 is invalid input, 429 `{prefix}_limit` is the daily limit
   * of the order and any other 429 the rate limit of the client address.
   */
  function outcomeOf(status, data, prefix) {
    const code = data && typeof data.code === 'string' ? data.code : '';
    if (status >= 200 && status < 300) return 'ok';
    if (status === 422 && code === `${prefix}_order_not_found`) return 'not_found';
    if (status === 422) return 'invalid';
    if (status === 429 && code === `${prefix}_limit`) return 'order_limit';
    if (status === 429) return 'rate_limited';
    if (status === 409) return 'conflict';
    return 'failed';
  }

  /**
   * Submission ids by statement, in sessionStorage under `storageKey`: a reload or a second click sends the same id, a
   * changed statement a new one. Without storage (private mode) the id lives in memory for this page.
   */
  function createSubmissions({storage, storageKey, uuid}) {
    // The statement itself (name, e-mail, account, a defect description) is the key; only its hash is kept in the tab.
    const hashOf = statement => {
      let h1 = 0xdeadbeef;
      let h2 = 0x41c6ce57;
      for (let index = 0; index < statement.length; index += 1) {
        const code = statement.charCodeAt(index);
        h1 = Math.imul(h1 ^ code, 2654435761);
        h2 = Math.imul(h2 ^ code, 1597334677);
      }
      h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
      h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
      return (h2 >>> 0).toString(16).padStart(8, '0') + (h1 >>> 0).toString(16).padStart(8, '0');
    };
    const HASH = /^[0-9a-f]{16}$/;
    const memory = {};
    const read = () => {
      try { return JSON.parse(storage?.getItem(storageKey) || '{}') || {}; } catch { return memory; }
    };
    const write = all => {
      try { storage?.setItem(storageKey, JSON.stringify(all)); } catch { Object.assign(memory, all); }
    };
    return {
      idFor(statement) {
        const key = hashOf(statement);
        const stored = {...memory, ...read()};
        // Keys from before the hash (the statement as text, BEST-558) give their id once and are not written back.
        const all = Object.fromEntries(Object.entries(stored).filter(([name]) => HASH.test(name)));
        if (!all[key]) {
          all[key] = typeof stored[statement] === 'string' ? stored[statement] : uuid();
          memory[key] = all[key];
        }
        if (Object.keys(stored).length !== Object.keys(all).length || !(key in stored)) write(all);
        return all[key];
      },
      forget(statement) {
        const key = hashOf(statement);
        const all = read();
        delete all[key];
        delete memory[key];
        write(all);
      },
      renew(statement) {
        this.forget(statement);
        return this.idFor(statement);
      },
    };
  }

  /**
   * Dates as the customer reads them in the page language: an instant (orderedAt, receivedAt) in the shop's time zone,
   * a calendar day (yyyy-MM-dd, dueOn) as that day wherever the browser is.
   */
  function createDateFormatter(locale) {
    return (iso, withTime) => {
      const day = /^\d{4}-\d{2}-\d{2}$/.test(String(iso));
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return iso;
      try {
        return new Intl.DateTimeFormat(locale, {dateStyle: 'long', ...(withTime && !day ? {timeStyle: 'short'} : {}),
          timeZone: day ? 'UTC' : 'Europe/Prague'}).format(date);
      } catch {
        return day ? date.toISOString().slice(0, 10) : date.toLocaleString();
      }
    };
  }

  /**
   * The order number a link hands over in the anchor (`#order=1302`, the customer account links „Reklamovat“ and
   * „Odstoupit od smlouvy“, BEST-562), as the customer knows it (`#1302`); null for any other anchor. Only the number
   * travels in the link, never the e-mail: the form still looks the order up by the number and the e-mail.
   */
  function orderFromLink(hash) {
    const match = /^#order=(?:%23|#)?(\d{1,9})$/.exec(String(hash || ''));
    return match ? `#${match[1]}` : null;
  }

  /**
   * Puts the linked order number into an empty order field of the form under `root`. The anchor `#order=` replaces the
   * form's own anchor (#reklamovat, #odstoupit), so the page is brought to the form here.
   */
  function prefillLinkedOrder(root, hash) {
    const order = orderFromLink(hash);
    const input = root.querySelector('[name="orderName"]');
    if (!order || !input) return;
    if (!input.value) input.value = order;
    root.scrollIntoView?.({block: 'start'});
  }

  /** The page side of a form: its texts rendered by the section, the date format of the page language, storage, ids. */
  function pageContext(root, i18nSelector) {
    let i18n = {};
    try { i18n = JSON.parse(root.querySelector(i18nSelector)?.textContent || '{}'); } catch { i18n = {}; }
    let storage = null;
    try { storage = window.sessionStorage; } catch { storage = null; }
    const uuid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
      }));
    return {
      t: key => i18n[key] || '',
      formatDate: createDateFormatter(root.dataset.locale || document.documentElement.lang || 'cs'),
      storage,
      uuid,
      fetchImpl: window.fetch.bind(window),
    };
  }

  // --- Support forms with photos (BEST-639) ---------------------------------------------------------------------------
  // The appliance request of the parts finder (bestio-appliance-finder.js) and the contact form (bestio-contact-form.js)
  // post multipart/form-data to /api/public/v1/appliance-requests and /api/public/v1/contact-messages. A multipart POST
  // without custom headers is a simple CORS request (no preflight); the Platform refuses a missing or foreign Origin
  // itself. The limits and codes below are the Platform's (bestio-hub Services/SupportRequests); the form checks the same
  // only to answer sooner, the server decides.
  const UPLOAD_TIMEOUT_MS = 60000;
  const PHOTO_LIMITS = Object.freeze({count: 3, fileBytes: 2 * 1024 * 1024, totalBytes: 6 * 1024 * 1024, minSide: 100,
    longestSide: 1600, quality: 0.82});
  // eslint-disable-next-line no-control-regex
  const CONTROL = /[\u0000-\u001f\u007f-\u009f]/;
  // eslint-disable-next-line no-control-regex
  const CONTROL_EXCEPT_LINES = /[\u0000-\u0009\u000b-\u001f\u007f-\u009f]/;
  const LINK = /\b(?:https?:\/\/|www\.)\S+/gi;

  /** POST of one multipart body to `path`; a response is {status, data, retryAfter}, an unreachable server throws NetworkError. */
  function createMultipartApi({baseUrl, path, fetchImpl, timeoutMs = UPLOAD_TIMEOUT_MS}) {
    const origin = String(baseUrl || '').replace(/\/+$/, '');
    return {
      async submit(body) {
        const controller = typeof AbortController === 'function' ? new AbortController() : null;
        const timer = controller && typeof setTimeout === 'function' ? setTimeout(() => controller.abort(), timeoutMs) : null;
        let response;
        try {
          // No Content-Type: the browser writes the multipart boundary itself.
          response = await fetchImpl(`${origin}${path}`, {method: 'POST', mode: 'cors', credentials: 'omit', cache: 'no-store',
            referrerPolicy: 'no-referrer', headers: {Accept: 'application/json'}, body, signal: controller?.signal});
        } catch (error) {
          throw new NetworkError(error?.name === 'AbortError' ? 'timeout' : 'network');
        } finally {
          if (timer) clearTimeout(timer);
        }
        let data = null;
        try {
          const answer = await response.text();
          data = answer ? JSON.parse(answer) : null;
        } catch {
          data = null;
        }
        return {status: response.status, data, retryAfter: retryAfterSeconds(response.headers?.get?.('Retry-After'))};
      },
    };
  }

  /** Seconds of a Retry-After header (seconds or an HTTP date), or null. */
  function retryAfterSeconds(value, now = Date.now()) {
    const header = text(value).trim();
    if (!header) return null;
    if (/^\d+$/.test(header)) return Number(header);
    const at = Date.parse(header);
    return Number.isNaN(at) ? null : Math.max(0, Math.ceil((at - now) / 1000));
  }

  /**
   * What an answer of a support form API means; `prefix` is its code prefix (appliance_request, contact_message).
   * 201 created, 200 the same submission again, 400 malformed, 403 foreign origin, 409 another content under the id, 413 too
   * large, 415 not multipart, 422 field errors, 429 rate limited, 503 not taken (nothing stored, or the e-mail failed).
   */
  function supportOutcome(status, data) {
    if (status === 201 || status === 200) return data && typeof data === 'object' ? 'ok' : 'failed';
    return {409: 'conflict', 413: 'too_large', 415: 'unsupported_media', 422: 'invalid', 429: 'rate_limited', 503: 'unavailable'}[status]
      || 'failed';
  }

  /**
   * Field errors of a ValidationProblemDetails (field → first code), keys as the server writes them: `model`, `email`,
   * `photos` and `photos[1]` (zero-based). A key in another case (Model) is read the same.
   */
  function serverFieldErrors(data) {
    const errors = {};
    for (const [key, codes] of Object.entries(data?.errors && typeof data.errors === 'object' ? data.errors : {})) {
      const code = Array.isArray(codes) ? text(codes[0]) : text(codes);
      const name = key.replace(/^[A-Z]/, letter => letter.toLowerCase());
      if (code) errors[name] = code;
    }
    return errors;
  }

  /**
   * A text as the server stores it: NFC, trimmed, runs of spaces as one; a multi-line text keeps its lines. `bad` is true
   * for a control character the server refuses (invalid_characters).
   */
  function normalizeText(value, {multiline = false} = {}) {
    let normalized = text(value).normalize('NFC').replace(/\r\n?/g, '\n');
    const bad = (multiline ? CONTROL_EXCEPT_LINES : CONTROL).test(normalized.replace(multiline ? /[\t ]/g : /[\t\n ]/g, ' '));
    normalized = multiline
      ? normalized.split('\n').map(line => line.replace(/\s+/g, ' ').trim()).join('\n').replace(/\n{3,}/g, '\n\n').trim()
      : normalized.replace(/\s+/g, ' ').trim();
    return {value: normalized, bad};
  }

  const countLinks = value => (text(value).match(LINK) || []).length;
  /** The server takes one value of at most 8 KiB in UTF-8, whatever its character limit (a message of emoji is shorter). */
  const VALUE_BYTES = 8 * 1024;
  const fitsValueBytes = value => new TextEncoder().encode(text(value)).length <= VALUE_BYTES;
  const isEmail = value => value.length <= 254 && /^[^\s@<>()",;:]+@[^\s@<>()",;:]+\.[^\s@<>()",;:.]{2,}$/.test(value);

  /** The size a photo is drawn at: the longest side at most `longestSide`, never enlarged. */
  function photoTargetSize(width, height, longestSide = PHOTO_LIMITS.longestSide) {
    const scale = Math.min(1, longestSide / Math.max(width, height));
    return {width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale))};
  }

  /**
   * The photo errors of the server's rules on prepared photos [{size, width, height}] (zero-based keys as the server's
   * photos[1]): each at most 2 MiB, together at most 6 MiB, at most 3, every side at least 100 px.
   */
  function photoErrors(photos) {
    const errors = {};
    const items = Array.isArray(photos) ? photos : [];
    if (items.length > PHOTO_LIMITS.count) errors.photos = 'too_many';
    items.forEach((photo, index) => {
      if (!(photo?.size > 0)) errors[`photos[${index}]`] = 'empty';
      else if (photo.size > PHOTO_LIMITS.fileBytes) errors[`photos[${index}]`] = 'file_too_large';
      else if (Math.min(photo.width, photo.height) < PHOTO_LIMITS.minSide) errors[`photos[${index}]`] = 'dimensions';
    });
    if (!errors.photos && items.reduce((sum, photo) => sum + (photo?.size || 0), 0) > PHOTO_LIMITS.totalBytes) errors.photos = 'total_too_large';
    return errors;
  }

  /**
   * Shrinks a chosen file in the browser: decoded, drawn at most 1600 px on the longest side and saved as JPEG 0.82,
   * which also leaves out EXIF and GPS. Resolves {blob, width, height, size, url} or {error} with the server's code
   * (unsupported_type for anything that is not a decodable image, dimensions for a side under 100 px).
   */
  async function preparePhoto(file, {document: page = globalThis.document, createImageBitmapImpl = globalThis.createImageBitmap} = {}) {
    if (!file || !/^image\//.test(text(file.type))) return {error: 'unsupported_type'};
    if (!(file.size > 0)) return {error: 'empty'};
    let source = null;
    try {
      source = createImageBitmapImpl ? await createImageBitmapImpl(file, {imageOrientation: 'from-image'}) : null;
    } catch {
      source = null;
    }
    if (!source) {
      source = await new Promise(resolve => {
        const image = new Image();
        const url = URL.createObjectURL(file);
        image.onload = () => { URL.revokeObjectURL(url); resolve(image); };
        image.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
        image.src = url;
      });
    }
    const width = source?.naturalWidth || source?.width || 0;
    const height = source?.naturalHeight || source?.height || 0;
    if (!width || !height) return {error: 'unsupported_type'};
    if (Math.min(width, height) < PHOTO_LIMITS.minSide) return {error: 'dimensions'};
    const target = photoTargetSize(width, height);
    const canvas = page.createElement('canvas');
    canvas.width = target.width;
    canvas.height = target.height;
    const context = canvas.getContext('2d');
    // A transparent PNG would turn black in a JPEG.
    context.fillStyle = '#fff';
    context.fillRect(0, 0, target.width, target.height);
    context.drawImage(source, 0, 0, target.width, target.height);
    source.close?.();
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', PHOTO_LIMITS.quality));
    if (!blob) return {error: 'unsupported_type'};
    if (blob.size > PHOTO_LIMITS.fileBytes) return {error: 'file_too_large'};
    return {blob, width: target.width, height: target.height, size: blob.size, url: URL.createObjectURL(blob)};
  }

  /**
   * The photos of one form: added files are prepared one by one, a refused file is reported with its code and not kept.
   * `prepare` is preparePhoto (a test passes its own).
   */
  function createPhotoList({prepare = preparePhoto, revoke = url => URL.revokeObjectURL(url)} = {}) {
    let items = [];
    return {
      get items() { return items; },
      /** Adds files up to the limit; resolves the refusals [{name, code}] (too_many for files over the limit). */
      async add(files) {
        const refused = [];
        for (const file of Array.from(files || [])) {
          if (items.length >= PHOTO_LIMITS.count) { refused.push({name: text(file?.name), code: 'too_many'}); continue; }
          const photo = await prepare(file);
          if (photo.error) refused.push({name: text(file?.name), code: photo.error});
          else if (items.reduce((sum, item) => sum + item.size, 0) + photo.size > PHOTO_LIMITS.totalBytes) {
            if (photo.url) revoke(photo.url);
            refused.push({name: text(file?.name), code: 'total_too_large'});
          } else items = [...items, photo];
        }
        return refused;
      },
      remove(index) {
        const removed = items[index];
        if (removed?.url) revoke(removed.url);
        items = items.filter((item, position) => position !== index);
      },
      clear() {
        for (const item of items) if (item.url) revoke(item.url);
        items = [];
      },
    };
  }

  /** The longest Retry-After of a 503 the support forms wait out by themselves before telling the customer (BEST-639). */
  const AUTO_RETRY_SECONDS = 30;

  /** The multipart body: the fields (empty ones left out) and the photos as photos foto-1.jpg …; the server names them again. */
  function supportFormData(fields, photos, FormDataImpl = globalThis.FormData) {
    const body = new FormDataImpl();
    for (const [name, value] of Object.entries(fields)) if (value != null && value !== '') body.append(name, String(value));
    (photos || []).forEach((photo, index) => body.append('photos', photo.blob, `foto-${index + 1}.jpg`));
    return body;
  }

  /**
   * One submission of a support form against its API, shared by the appliance request and the contact form. `validate`
   * turns the form values into {value, errors}; `fields` turns the value into the API fields; `fingerprint` makes one
   * stable text of what is sent, so a retry (a lost answer, a double click, the photos sent again) reuses the submission id
   * and the server answers 200 with the first receipt. The view gets field errors, a message with the e-mail fallback, or
   * the receipt.
   */
  function createSupportFormFlow({api, view, submissions, validate, fields, fingerprint, mailto, photosMailto, FormDataImpl,
    sleep = seconds => new Promise(resolve => setTimeout(resolve, seconds * 1000))}) {
    const state = {busy: false, last: null};

    const failure = (kind, value, extra = {}) => view.showMessage(kind, {
      mailto: ['failed', 'unavailable', 'conflict', 'rate_limited'].includes(kind) ? mailto(value) : null,
      ...extra,
    });

    async function send(values, photos = []) {
      if (state.busy) return 'busy';
      view.clearMessage();
      const {value, errors} = validate(values);
      Object.assign(errors, photoErrors(photos));
      view.showErrors(errors);
      if (Object.keys(errors).length > 0) return 'invalid';
      const key = fingerprint(value, photos.map(photo => [photo.size, photo.width, photo.height]));
      const submissionId = submissions.idFor(key);
      state.last = {values, photos};
      state.busy = true;
      view.setBusy(true);
      try {
        const body = supportFormData({submissionId, ...fields(value)}, photos, FormDataImpl);
        let response = await api.submit(body);
        // A short 503 (the first sending of this submission's photos is still running, BEST-639) is waited out once under the
        // same id, still busy, before the customer is told anything; a longer one or a second 503 is a real outage.
        if (response.status === 503 && response.retryAfter != null && response.retryAfter <= AUTO_RETRY_SECONDS) {
          await sleep(response.retryAfter);
          response = await api.submit(body);
        }
        const outcome = supportOutcome(response.status, response.data);
        if (outcome === 'ok') {
          const photosState = ['delivered', 'failed', 'none'].includes(response.data.photos) ? response.data.photos : 'none';
          const reference = text(response.data.reference);
          // Photos that did not reach support are sent again under the same id; anything else closes the submission.
          if (photosState !== 'failed') submissions.forget(key);
          view.showDone({reference, again: response.status === 200, photos: photosState, value,
            photosMailto: photosState === 'failed' && reference ? photosMailto(reference, value) : null});
          return photosState === 'failed' ? 'photos_failed' : 'done';
        }
        if (outcome === 'invalid') {
          const fieldErrors = serverFieldErrors(response.data);
          const known = Object.fromEntries(Object.entries(fieldErrors).filter(([name]) => view.knowsField(name)));
          if (Object.keys(known).length > 0) view.showErrors(known);
          else failure('failed', value);
        } else if (outcome === 'too_large') view.showErrors({photos: 'total_too_large'});
        else if (outcome === 'unsupported_media') view.showErrors({photos: 'unsupported_type'});
        else if (outcome === 'conflict') {
          // Another content went under this id before (the form changed after a lost answer); the next try is a new one.
          submissions.renew(key);
          failure('conflict', value);
        } else if (outcome === 'rate_limited') failure('rate_limited', value, {retryAfter: response.retryAfter});
        else failure(outcome === 'unavailable' ? 'unavailable' : 'failed', value);
        return outcome;
      } catch (error) {
        if (!(error instanceof NetworkError)) throw error;
        failure('failed', value);
        return 'network';
      } finally {
        state.busy = false;
        view.setBusy(false);
      }
    }

    /** The last submission once more (the photos that did not arrive), under the same id. */
    const resend = () => (state.last ? send(state.last.values, state.last.photos) : Promise.resolve('none'));

    return {send, resend, state};
  }

  window.BestioPublicForm = {format, text, NetworkError, createApi, outcomeOf, createSubmissions, createDateFormatter, pageContext, orderFromLink,
    prefillLinkedOrder, PHOTO_LIMITS, createMultipartApi, retryAfterSeconds, supportOutcome, serverFieldErrors, normalizeText, countLinks,
    isEmail, VALUE_BYTES, fitsValueBytes, photoTargetSize, photoErrors, preparePhoto, createPhotoList, supportFormData, createSupportFormFlow};
})();
